/**
 * GroupMembershipService - Service class for group membership operations
 * 
 * Follows SOLID principles:
 * - Single Responsibility: Only handles group membership operations
 * - Open/Closed: Can be extended without modifying existing code
 * - Liskov Substitution: Can be substituted with other membership services
 * - Interface Segregation: Focused interface for group membership only
 * - Dependency Inversion: Depends on abstractions (apiClient) not concretions
 */
class GroupMembershipService {
    constructor(apiClient) {
        this.apiClient = apiClient
    }

    /**
     * Add a member to a group
     * @param {string} groupId - Group ID
     * @param {string} memberId - Member ID
     * @returns {Promise<Object>} API response
     */
    async addMemberToGroup(groupId, memberId) {
        if (!groupId || !memberId) {
            throw new Error('Group ID and Member ID are required')
        }

        try {
            const response = await this.apiClient.addMemberToGroup(groupId, memberId)
            return {
                success: true,
                data: response,
                message: response.message || 'Member added to group successfully'
            }
        } catch (error) {
            return {
                success: false,
                error: error.message || 'Failed to add member to group'
            }
        }
    }

    /**
     * Remove a member from a group
     * @param {string} groupId - Group ID
     * @param {string} memberId - Member ID
     * @returns {Promise<Object>} API response
     */
    async removeMemberFromGroup(groupId, memberId) {
        if (!groupId || !memberId) {
            throw new Error('Group ID and Member ID are required')
        }

        try {
            const response = await this.apiClient.removeMemberFromGroup(groupId, memberId)
            return {
                success: true,
                data: response,
                message: response.message || 'Member removed from group successfully'
            }
        } catch (error) {
            return {
                success: false,
                error: error.message || 'Failed to remove member from group'
            }
        }
    }

    /**
     * Get groups for a specific member
     * @param {Array} allGroups - All available groups
     * @param {string} memberId - Member ID
     * @returns {Array} Groups the member belongs to
     */
    getMemberGroups(allGroups, memberId) {
        if (!Array.isArray(allGroups) || !memberId) {
            return []
        }

        return allGroups.filter(group => 
            group.members && group.members.some(member => member.id === memberId)
        )
    }

    /**
     * Get available groups for a member to join
     * @param {Array} allGroups - All available groups
     * @param {Array} memberGroups - Groups the member is already in
     * @returns {Array} Groups available to join
     */
    getAvailableGroupsToJoin(allGroups, memberGroups) {
        if (!Array.isArray(allGroups) || !Array.isArray(memberGroups)) {
            return []
        }

        return allGroups.filter(group => 
            !memberGroups.some(memberGroup => memberGroup.id === group.id)
        )
    }

    /**
     * Create confirmation message for removing member from group
     * @param {Object} member - Member object
     * @param {Object} group - Group object
     * @returns {string} Confirmation message
     */
    createRemoveConfirmationMessage(member, group) {
        const memberName = member?.full_name || member?.display_name || member?.email || 'this member'
        const groupName = group?.name || 'the group'
        
        return `Are you sure you want to remove ${memberName} from ${groupName}?`
    }
}

export default GroupMembershipService